/**
 * Local-only persistence. Everything here lives in this browser's
 * localStorage on this device and never leaves it. Nothing about the photos
 * is stored: the consent log holds timestamps, the reference the user typed,
 * export settings, and a SHA-256 fingerprint of the exported file so a log
 * entry can be matched to a clip later without keeping the clip.
 */
import type { AspectKey, ExportMode } from './state';

const SETTINGS_KEY = 'beforeAfterSlider.settings.v1';
const LOGO_KEY = 'beforeAfterSlider.logo.v1';
const LOG_KEY = 'beforeAfterSlider.consentLog.v1';
/** Oldest entries are dropped beyond this. */
const LOG_LIMIT = 500;

export type BrandingCorner = 'bottom-left' | 'bottom-right';

export interface Settings {
  disclaimerEnabled: boolean;
  disclaimerText: string;
  brandingEnabled: boolean;
  /** Handle or practice name, e.g. "@saenzaesthetics". */
  brandingText: string;
  brandingCorner: BrandingCorner;
  /** Logo width as a fraction of the frame width. */
  brandingLogoScale: number;
}

export const DEFAULT_SETTINGS: Settings = {
  disclaimerEnabled: true,
  disclaimerText: 'Results may vary',
  brandingEnabled: true,
  brandingText: '',
  brandingCorner: 'bottom-right',
  brandingLogoScale: 0.2,
};

export interface ConsentEntry {
  id: string;
  /** ISO timestamp of when the export finished. */
  exportedAt: string;
  /** ISO timestamp of when the consent box was ticked for this photo pair. */
  consentConfirmedAt: string;
  /** Chart number, initials, or whatever the user typed. May be empty. */
  reference: string;
  mode: ExportMode;
  aspect: AspectKey;
  durationMs: number;
  fileBytes: number;
  /** SHA-256 of the exported MP4, hex. */
  sha256: string;
  /** Caption burned into the export, if any. */
  disclaimer: string | null;
  /** Branding burned into the export: the handle text, "logo", or both; null if none. */
  branding: string | null;
  /** Number of angles (photo sets) included in this export. 1 for a normal export. */
  angles: number;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    // private mode, blocked storage, or corrupt JSON: behave as empty
    return null;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

export function loadSettings(): Settings {
  const s = read<Partial<Settings>>(SETTINGS_KEY) ?? {};
  const d = DEFAULT_SETTINGS;
  return {
    disclaimerEnabled: typeof s.disclaimerEnabled === 'boolean' ? s.disclaimerEnabled : d.disclaimerEnabled,
    disclaimerText: typeof s.disclaimerText === 'string' ? s.disclaimerText : d.disclaimerText,
    brandingEnabled: typeof s.brandingEnabled === 'boolean' ? s.brandingEnabled : d.brandingEnabled,
    brandingText: typeof s.brandingText === 'string' ? s.brandingText : d.brandingText,
    brandingCorner: s.brandingCorner === 'bottom-left' || s.brandingCorner === 'bottom-right' ? s.brandingCorner : d.brandingCorner,
    brandingLogoScale:
      typeof s.brandingLogoScale === 'number' && s.brandingLogoScale >= 0.05 && s.brandingLogoScale <= 0.5 ? s.brandingLogoScale : d.brandingLogoScale,
  };
}

export function saveSettings(settings: Settings): boolean {
  return write(SETTINGS_KEY, settings);
}

/** The logo is kept under its own key so typing in other fields never rewrites it. */
export function loadLogoDataUrl(): string | null {
  try {
    const v = localStorage.getItem(LOGO_KEY);
    return v && v.startsWith('data:image/') ? v : null;
  } catch {
    return null;
  }
}

export function saveLogoDataUrl(dataUrl: string | null): boolean {
  try {
    if (dataUrl) localStorage.setItem(LOGO_KEY, dataUrl);
    else localStorage.removeItem(LOGO_KEY);
    return true;
  } catch {
    return false;
  }
}

export function loadConsentLog(): ConsentEntry[] {
  const entries = read<ConsentEntry[]>(LOG_KEY);
  return Array.isArray(entries) ? entries : [];
}

export function appendConsentEntry(entry: ConsentEntry): boolean {
  const entries = loadConsentLog();
  entries.push(entry);
  return write(LOG_KEY, entries.slice(-LOG_LIMIT));
}

export function clearConsentLog(): boolean {
  try {
    localStorage.removeItem(LOG_KEY);
    return true;
  } catch {
    return false;
  }
}

export function newEntryId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Hex SHA-256 of a blob. Web Crypto needs a secure context; degrades to a note. */
export async function sha256Hex(blob: Blob): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return 'unavailable';
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

function csvCell(v: string | number | null): string {
  const s = v === null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function consentLogToCsv(entries: readonly ConsentEntry[]): string {
  const header = ['exported_at', 'consent_confirmed_at', 'reference', 'mode', 'aspect', 'angles', 'duration_seconds', 'file_bytes', 'sha256', 'disclaimer', 'branding'];
  const rows = entries.map((e) =>
    [e.exportedAt, e.consentConfirmedAt, e.reference, e.mode, e.aspect, e.angles, (e.durationMs / 1000).toFixed(2), e.fileBytes, e.sha256, e.disclaimer, e.branding ?? null]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...rows].join('\r\n') + '\r\n';
}
